import { atom, useAtomValue, useSetAtom } from 'jotai'
import { atomWithStorage } from 'jotai/utils'
import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { rundownAtom } from '@/store/rundown.store.ts'
import { pruneColumnLayout } from '@/shared/lib/prune-column-layout.ts'
import type { RundownColumn } from '@rundown-studio/types'

const STORAGE_KEY = 'rundown-column-layout'

export interface ColumnLayout {
  columnWidths: Record<string, number>
  columnOrder: string[]
  hiddenColumns: string[]
}

type ColumnLayoutMap = Record<string, ColumnLayout>

const emptyLayout: ColumnLayout = {
  columnWidths: {},
  columnOrder: [],
  hiddenColumns: [],
}

// localStorage-backed map, keyed by rundownId.
// Entries only exist once a user modifies the layout — there is no proactive
// seeding, which keeps this feature effect-free. Readers fall back to the
// rundown's own column order / widths when no entry exists.
export const columnLayoutMapAtom = atomWithStorage<ColumnLayoutMap>(
  STORAGE_KEY,
  {},
)

// Internal write-only atom: patch a specific rundown's layout entry.
const _patchColumnLayoutAtom = atom(
  null,
  (
    get,
    set,
    { rundownId, patch }: { rundownId: string; patch: Partial<ColumnLayout> },
  ) => {
    const map = get(columnLayoutMapAtom)
    const current = map[rundownId] ?? emptyLayout
    let merged: ColumnLayout = { ...current, ...patch }
    const rundown = get(rundownAtom)
    if (rundown && rundown.id === rundownId) {
      merged = pruneColumnLayout(merged, new Set(rundown.columns ?? []))
    }
    set(columnLayoutMapAtom, {
      ...map,
      [rundownId]: merged,
    })
  },
)

// Internal write-only atom: merge a single column width into a rundown's entry.
const _setColumnLayoutWidthAtom = atom(
  null,
  (
    get,
    set,
    {
      rundownId,
      columnId,
      width,
    }: { rundownId: string; columnId: string; width: number },
  ) => {
    const map = get(columnLayoutMapAtom)
    const current = map[rundownId] ?? emptyLayout
    set(columnLayoutMapAtom, {
      ...map,
      [rundownId]: {
        ...current,
        columnWidths: { ...current.columnWidths, [columnId]: width },
      },
    })
  },
)

// Internal write-only atom: remove a rundown's entry entirely.
const _resetColumnLayoutAtom = atom(
  null,
  (get, set, rundownId: string) => {
    const map = get(columnLayoutMapAtom)
    if (!(rundownId in map)) return
    const next = { ...map }
    delete next[rundownId]
    set(columnLayoutMapAtom, next)
  },
)

/**
 * Read the raw stored layout entry for a rundown (empty defaults if missing).
 * Does NOT apply any reconciliation against current rundown columns.
 */
export function useRawColumnLayout(rundownId: string | undefined): ColumnLayout {
  const map = useAtomValue(columnLayoutMapAtom)
  if (!rundownId) return emptyLayout
  return map[rundownId] ?? emptyLayout
}

/**
 * Effective column order = user's saved order, reconciled against current
 * rundown columns (removed columns dropped, new columns appended). Falls back
 * to rundown.columns when no saved order exists.
 */
export function useEffectiveColumnOrder(rundownId: string | undefined): string[] {
  const rundown = useAtomValue(rundownAtom)
  const layout = useRawColumnLayout(rundownId)

  return useMemo(() => {
    const baseOrder = rundown?.columns ?? []
    if (!layout.columnOrder.length) return baseOrder
    const baseSet = new Set(baseOrder)
    const reconciled = layout.columnOrder.filter((id) => baseSet.has(id))
    const userSet = new Set(reconciled)
    const appended = baseOrder.filter((id) => !userSet.has(id))
    return [...reconciled, ...appended]
  }, [rundown?.columns, layout.columnOrder])
}

/**
 * Effective hidden columns = user's local hidden set, merged with URL-forced
 * hidden columns from `?hiddenColumns=…` (URL always wins).
 */
export function useEffectiveHiddenColumns(
  rundownId: string | undefined,
): string[] {
  const layout = useRawColumnLayout(rundownId)
  const [searchParams] = useSearchParams()

  return useMemo(() => {
    const urlHidden = searchParams.has('hiddenColumns')
      ? (searchParams.get('hiddenColumns')?.split(',').filter(Boolean) ?? [])
      : []
    if (!urlHidden.length) return layout.hiddenColumns
    return [...new Set([...layout.hiddenColumns, ...urlHidden])]
  }, [layout.hiddenColumns, searchParams])
}

/**
 * URL-forced hidden columns only (everything the admin force-hid via the
 * share link). Used by "Hidden columns" menus to filter out entries the
 * user can't unhide locally.
 */
export function useUrlHiddenColumns(): string[] {
  const [searchParams] = useSearchParams()
  return useMemo(() => {
    if (!searchParams.has('hiddenColumns')) return []
    return searchParams.get('hiddenColumns')?.split(',').filter(Boolean) ?? []
  }, [searchParams])
}

/**
 * Apply user width overrides (from localStorage) to a base list of columns.
 * Pure function so it can be used in atoms, memos, or inline.
 */
export function applyColumnWidthOverrides(
  columns: RundownColumn[],
  widthOverrides: Record<string, number>,
): RundownColumn[] {
  return columns.map((column) =>
    widthOverrides[column.id] !== undefined
      ? { ...column, width: widthOverrides[column.id] }
      : column,
  )
}

/**
 * Returns the list of columns with the user's local order + width overrides
 * applied. The base `columns` input should be sorted by rundown.columns DB
 * order (i.e. the value of `columnsAtom`).
 */
export function useColumnLayoutColumns(
  rundownId: string | undefined,
  columns: RundownColumn[],
): RundownColumn[] {
  const layout = useRawColumnLayout(rundownId)
  const effectiveOrder = useEffectiveColumnOrder(rundownId)

  return useMemo(() => {
    const orderIndex = new Map(effectiveOrder.map((id, i) => [id, i]))
    const sorted = columns
      .filter((c) => orderIndex.has(c.id))
      .sort(
        (a, b) =>
          (orderIndex.get(a.id) ?? 0) - (orderIndex.get(b.id) ?? 0),
      )
    return applyColumnWidthOverrides(sorted, layout.columnWidths)
  }, [columns, effectiveOrder, layout.columnWidths])
}

/**
 * Bundled writer hook for a specific rundown — all layout mutations for that
 * rundown. Prefer this over the lower-level atoms in consumers.
 */
export function useColumnLayoutActions(rundownId: string | undefined) {
  const patch = useSetAtom(_patchColumnLayoutAtom)
  const setWidth = useSetAtom(_setColumnLayoutWidthAtom)
  const reset = useSetAtom(_resetColumnLayoutAtom)

  const patchLayout = useCallback(
    (p: Partial<ColumnLayout>) => {
      if (!rundownId) return
      patch({ rundownId, patch: p })
    },
    [patch, rundownId],
  )

  const setColumnWidth = useCallback(
    (columnId: string, width: number) => {
      if (!rundownId) return
      setWidth({ rundownId, columnId, width })
    },
    [setWidth, rundownId],
  )

  const resetLayout = useCallback(() => {
    if (!rundownId) return
    reset(rundownId)
  }, [reset, rundownId])

  return { patchLayout, setColumnWidth, resetLayout }
}
